import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment
} from '@mui/material';
import Select, { createFilter } from 'react-select';
import { Close as CloseIcon } from '@mui/icons-material';
import ActionButton from './ActionButton';
import Toggle from './Toggle';
import AmountInput from './AmountInput';
import { CustomDropdownIndicator, MultiselectValueContainer, MultiselectOption, MultiselectMenuList } from './MultiselectComponents';
import { useMultiselectMenuControl } from '../../hooks/useMultiselectMenuControl';

const CreationModal = ({
  open,
  onClose,
  onSubmit,
  title,
  fields = [],
  submitButtonText = "Create",
  loading = false,
  // New props for enhanced functionality
  toggleConfig = null, // { field: 'active', label: 'Activé/Désactivé' }
  externalErrors = {}, // External error handling
  initialData = {}, // Initial form data
  onFieldChange = null, // Callback for field changes (for dynamic fields)
  showPasswordToggle = true, // Show password visibility toggle
  showLabels = false, // Show field labels above inputs
  fieldsPerRow = 3 // Number of fields per row (default 3 for backward compatibility)
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({});
  const firstInputRef = useRef(null);
  
  // Track actual caisse IDs separately for special options handling
  const [actualCaisseIds, setActualCaisseIds] = useState([]);
  const specialOptionHandledRef = useRef(false);
  
  // Use shared hook for multiselect menu control
  const { menuIsOpen, setMenuIsOpen, selectWrapperRef, lastActionRef } = useMultiselectMenuControl(true);

  // Resolve fields (handle both array and function) - make it reactive to formData changes
  const resolvedFields = useMemo(() => {
    return typeof fields === 'function' ? fields(formData) : fields;
  }, [fields, formData]);


  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      // Clear scroll position when modal closes
      if (typeof window !== 'undefined') {
        window.__multiselectScrollPosition = null;
      }
    }
    
    if (open) {
      // Start with initialData
      let newFormData = { ...initialData };
      
      // Set default values for fields that don't exist yet
      // Use the original fields instead of resolvedFields to avoid circular dependency
      const fieldsToUse = typeof fields === 'function' ? fields(initialData) : fields;
      fieldsToUse.forEach(field => {
        if (newFormData[field.name] === undefined) {
          newFormData[field.name] = field.defaultValue || '';
        }
      });
      
      // Ensure active field is preserved
      if (initialData.active !== undefined) {
        newFormData.active = initialData.active;
      }
      
      setFormData(newFormData);
      setErrors({});
      
      // Initialize actualCaisseIds from initialData if it's a caisseIds field
      if (initialData.caisseIds && Array.isArray(initialData.caisseIds)) {
        setActualCaisseIds(initialData.caisseIds);
      } else {
        setActualCaisseIds([]);
      }
      
      // Auto-focus the first input field
      setTimeout(() => {
        if (firstInputRef.current) {
          // Check if it's a react-select component
          if (firstInputRef.current.focus) {
            // For react-select, focus the input element
            firstInputRef.current.focus();
          } else if (firstInputRef.current.inputRef) {
            // For Material-UI TextField
            firstInputRef.current.inputRef.focus();
          }
        }
      }, 100); // Small delay to ensure modal is fully rendered
      setShowPassword({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]); // Intentionally excluding 'fields' to prevent infinite loop


  // Helper to check if multiselect has value
  const hasMultiselectValue = (value) => {
    return Array.isArray(value) && value.length > 0;
  };

  // Enhanced input change handler
  const handleInputChange = (fieldName, value) => {
    // Find the field configuration to check for transform function
    const field = resolvedFields.find(f => f.name === fieldName);
    
    // Apply transform if defined
    let transformedValue = value;
    if (field && field.transform && typeof field.transform === 'function') {
      transformedValue = field.transform(value);
    }
    
    const newFormData = { ...formData, [fieldName]: transformedValue };
    setFormData(newFormData);
    
    // Clear error when user starts typing
    if (errors[fieldName] || externalErrors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }

    // Call external field change callback for dynamic fields
    if (onFieldChange) {
      onFieldChange(fieldName, transformedValue, newFormData);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (fieldName) => {
    setShowPassword(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  // Reusable error display component
  const renderErrorDisplay = (fieldName) => {
    const hasError = !!(errors[fieldName] || externalErrors[fieldName]);
    const errorMessage = errors[fieldName] || externalErrors[fieldName];
    
    if (!hasError) return null;
    
    return (
      <div className="flex items-center mt-2">
        <svg 
          className="w-4 h-4 text-[#EF466F] mr-2 flex-shrink-0" 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <p className="text-[#EF466F] text-xs">{errorMessage}</p>
      </div>
    );
  };

  const getCustomSelectStyles = (fieldName, isMultiselect = false) => ({
    control: (provided, state) => {
      const hasError = !!(errors[fieldName] || externalErrors[fieldName]);
      // Fix hasValue check for dropdowns - handle numeric values properly
      const hasValue = formData[fieldName] !== null && 
                      formData[fieldName] !== undefined && 
                      formData[fieldName] !== '' &&
                      !(Array.isArray(formData[fieldName]) && formData[fieldName].length === 0);
      
      return {
        ...provided,
        backgroundColor: '#ffffff',
        border: hasError ? '2px solid #EF456E' : (state.isFocused || hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3'),
        borderRadius: '8px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'Montserrat, sans-serif',
        paddingLeft: '12px',
        paddingRight: '12px',
        '&:hover': {
          border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
        },
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      height: '48px',
      minHeight: '48px',
      overflow: 'hidden',
      flex: 1,
      justifyContent: 'flex-start',
      flexWrap: 'nowrap',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#000000',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: '#38485C',
      padding: '0',
      minWidth: '20px',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
      marginTop: '4px',
      zIndex: 999999,
      overflow: 'hidden',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0',
    }),
    option: (provided, state) => {
      const isCaisseMultiselect = fieldName === 'caisseIds' && isMultiselect;
      return {
        ...provided,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 20px',
        backgroundColor: state.isSelected
          ? '#F0F8FF'
          : state.isFocused
            ? '#F3F4F6'
            : '#ffffff',
        ...(isCaisseMultiselect ? {} : {
          color: state.isSelected ? '#027BC6' : '#333',
          fontSize: '16px',
          fontWeight: state.isSelected ? 700 : 600,
        }),
        fontFamily: 'Montserrat, sans-serif',
        cursor: 'pointer',
        borderBottom: '1px solid #E4E7EA',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: state.isSelected ? '#E6F3FF' : '#F3F4F6',
        },
      };
    },
    placeholder: (provided) => ({
      ...provided,
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      color: '#A3A3A3',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 999999,
    }),
  });


  const validateForm = () => {
    const newErrors = {};
    
    resolvedFields.forEach(field => {
      // Handle different field types for validation
      let isEmpty = false;
      
      if (field.type === 'select' || field.type === 'multiselect') {
        // For dropdowns, check if value is null, undefined, empty string, or empty array
        isEmpty = formData[field.name] === null || 
                  formData[field.name] === undefined || 
                  formData[field.name] === '' ||
                  (Array.isArray(formData[field.name]) && formData[field.name].length === 0);
      } else {
        // For text fields, check if empty after trimming
        isEmpty = !formData[field.name] || formData[field.name].toString().trim() === '';
      }
      
      if (field.required && isEmpty) {
        newErrors[field.name] = 'Le champ est requis';
      }
      
      // Password validation - minimum 6 characters
      if (field.type === 'password' && formData[field.name] && formData[field.name].length < 6) {
        newErrors[field.name] = 'Le mot de passe doit contenir au moins 6 caractères';
      }
      
      // Amount validation - must be positive
      if (field.type === 'number' && formData[field.name]) {
        const value = parseFloat(formData[field.name]);
        if (isNaN(value) || value <= 0) {
          newErrors[field.name] = 'Le montant doit être un nombre positif';
        }
      }
      
      // Caisse validation for users
      if (field.name === 'caisseIds') {
        const caisseIdsToCheck = actualCaisseIds.length > 0 ? actualCaisseIds : (formData.caisseIds || []);
        const selectedRole = formData.role?.value || formData.role;
        if (selectedRole === 'AGENT' && caisseIdsToCheck.length === 0) {
          newErrors.caisseIds = 'La caisse est requise pour un agent';
        // Legacy VERIFICATION role - commented out after migration
        // } else if (selectedRole === 'VERIFICATION' && caisseIdsToCheck.length === 0) {
        //   newErrors.caisseIds = 'Au moins une caisse est requise pour un agent de vérification';
        } else if ((selectedRole === 'POINTAGE_CLIENT' || selectedRole === 'POINTAGE_COMPTABILITE') && caisseIdsToCheck.length === 0) {
          newErrors.caisseIds = 'Au moins une caisse est requise pour un agent de pointage';
        }
      }
      
      // Custom validation function
      if (field.validation && typeof field.validation === 'function' && formData[field.name]) {
        const validationError = field.validation(formData[field.name]);
        if (validationError) {
          newErrors[field.name] = validationError;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Get field label text
  const getFieldLabel = (fieldName) => {
    const labelMap = {
      'label': 'LIBELLÉ',
      'firstName': 'PRÉNOM',
      'lastName': 'NOM',
      'username': 'NOM D\'UTILISATEUR',
      'password': 'NOUVEAU MOT DE PASSE',
      'role': 'RÔLE',
      'caisseIds': 'CAISSE',
      'caisseFactor': 'EFFET CAISSE',
      'coffreFactor': 'EFFET COFFRE',
      'notifyManager': 'NOTIFICATIONS MANAGER',
      'requiresPointage': 'NÉCESSSITE POINTAGE',
      'requiresPointageClient': 'POINTAGE CLIENT',
      'requiresPointageComptabilite': 'POINTAGE COMPTABILITÉ',
      'name': 'NOM',
      'amount': 'MONTANT',
      'assignTo': 'ASSIGNÉ À',
      'note': 'NOTE'
    };
    return labelMap[fieldName] || fieldName.toUpperCase();
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // For caisseIds field, use actualCaisseIds instead of formData.caisseIds
      const submitData = { ...formData };
      if (actualCaisseIds.length > 0) {
        submitData.caisseIds = actualCaisseIds;
      }
      onSubmit(submitData);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  const handleKeyDown = (e) => {
    // Letting the dialog-level handler manage Enter key behavior
    // This prevents double submission
  };

  const renderField = (field, index) => {
    // Fix hasValue check for all field types
    const hasValue = formData[field.name] !== null && 
                    formData[field.name] !== undefined && 
                    formData[field.name] !== '' &&
                    !(Array.isArray(formData[field.name]) && formData[field.name].length === 0);
    const hasError = !!(errors[field.name] || externalErrors[field.name]);
    const errorMessage = errors[field.name] || externalErrors[field.name];

    const commonProps = {
      fullWidth: true,
      value: formData[field.name] || '',
      onChange: (e) => handleInputChange(field.name, e.target.value),
      onKeyDown: handleKeyDown,
      error: hasError,
      helperText: errorMessage,
      inputRef: index === 0 ? firstInputRef : null, // Add ref to first field
      sx: {
        '& .MuiInputBase-input': {
          fontFamily: 'Montserrat',
          fontSize: '14px',
          fontWeight: 600,
          color: '#333',
          textTransform: 'none'
        },
        '& .MuiInputLabel-root': {
          fontFamily: 'Montserrat',
          fontSize: '14px',
          fontWeight: 600,
          color: '#AFB0B2',
          textTransform: 'none'
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#FFFFFF',
          '& fieldset': {
            borderColor: '#E0E0E0'
          },
          '&:hover fieldset': {
            borderColor: '#027BC6'
          },
          '&.Mui-focused fieldset': {
            borderColor: '#027BC6'
          }
        }
      }
    };

    switch (field.type) {
      case 'select':
        return (
          <div key={field.name} className="w-full">
            {showLabels && (
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#6B7280',
                  textTransform: 'none',
                  marginBottom: '8px',
                  letterSpacing: '0.5px'
                }}
              >
                {getFieldLabel(field.name)}
              </Typography>
            )}
            <Select
              ref={index === 0 ? firstInputRef : null} // Add ref to first dropdown
              options={field.options?.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              value={field.options?.find(option => option.value === formData[field.name]) || null}
              onChange={(selectedOption) => handleInputChange(field.name, selectedOption ? selectedOption.value : '')}
              placeholder={field.placeholder}
              styles={getCustomSelectStyles(field.name, false)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              components={{
                DropdownIndicator: (props) => <CustomDropdownIndicator {...props} hasValue={!!formData[field.name]} onClear={() => handleInputChange(field.name, '')} />,
              }}
            />
            {renderErrorDisplay(field.name)}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="w-full">
            {showLabels && (
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#6B7280',
                  textTransform: 'none',
                  marginBottom: '8px',
                  letterSpacing: '0.5px'
                }}
              >
                {getFieldLabel(field.name)}
              </Typography>
            )}
            <TextField
              fullWidth
              value={formData[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={index === 0 ? firstInputRef : null}
              multiline
              rows={3}
              placeholder={field.placeholder}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  border: (formData[field.name] && formData[field.name].toString().trim() !== '') ? '2px solid #007BC6' : '1px solid #E3E3E3',
                  borderRadius: '12px',
                  padding: '0',
                  height: 'auto',
                  minHeight: '72px',
                  maxHeight: '120px',
                  overflow: 'auto',
                  '&:hover': {
                    border: '1px solid #007BC6',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #007BC6',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  width: '100%',
                  minHeight: '48px',
                  maxHeight: '96px',
                  padding: '12px',
                  border: 'none',
                  outline: 'none',
                  borderRadius: '12px',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#000000',
                  backgroundColor: 'transparent',
                  resize: 'none',
                  boxSizing: 'border-box',
                  overflow: 'auto',
                  '&::placeholder': {
                    color: '#A3A3A3',
                    opacity: 1,
                  },
                },
                '& .MuiInputLabel-root': {
                  display: 'none',
                },
              }}
            />
            {renderErrorDisplay(field.name)}
          </div>
        );

      case 'number':
        return (
          <div key={field.name} className="w-full">
            {showLabels && (
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#6B7280',
                  textTransform: 'none',
                  marginBottom: '8px',
                  letterSpacing: '0.5px'
                }}
              >
                {getFieldLabel(field.name)}
              </Typography>
            )}
            <AmountInput
              {...commonProps}
              placeholder={field.placeholder}
              error={hasError}
              helperText=""
            />
            {renderErrorDisplay(field.name)}
          </div>
        );

      case 'password':
        return (
          <div key={field.name} className="w-full">
            {showLabels && (
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#6B7280',
                  textTransform: 'none',
                  marginBottom: '8px',
                  letterSpacing: '0.5px'
                }}
              >
                {getFieldLabel(field.name)}
              </Typography>
            )}
            <TextField
              {...commonProps}
              type={showPassword[field.name] ? 'text' : 'password'}
              placeholder={field.placeholder}
              autoComplete="new-password"
              inputProps={{
                autoComplete: 'new-password',
                'data-form-type': 'other'
              }}
              InputProps={{
                endAdornment: showPasswordToggle ? (
                  <InputAdornment position="end" sx={{ mr: 1 }}>
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => togglePasswordVisibility(field.name)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        fill="none" 
                        stroke="#000000" 
                        viewBox="0 0 24 24"
                      >
                        {showPassword[field.name] ? (
                          <>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                          </>
                        ) : (
                          <>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                ...commonProps.sx,
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  paddingLeft: '12px',
                  paddingRight: showPasswordToggle ? '4px' : '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  backgroundColor: '#ffffff',
                  border: hasError ? '2px solid #EF456E' : (hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3'),
                  borderRadius: '8px',
                  '&:hover': {
                    border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
                  },
                  '&.Mui-focused': {
                    border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '0 !important',
                  '&::placeholder': {
                    color: '#A3A3A3',
                    opacity: 1,
                  },
                },
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
            />
            {renderErrorDisplay(field.name)}
          </div>
        );

      case 'multiselect': {
        const isCaisseField = field.name === 'caisseIds';
        const mobileCaisses = isCaisseField ? (field.mobileCaisses || []) : [];
        const physiqueCaisses = isCaisseField ? (field.physiqueCaisses || []) : [];
        
        // Use actualCaisseIds for caisse field, formData for others
        const currentIds = isCaisseField ? actualCaisseIds : (formData[field.name] || []);
        const multiselectHasValue = hasMultiselectValue(currentIds);
        
        // Build selected options - exclude special options from react-select's value
        // Special options are visual indicators only, handled separately via onSpecialOptionClick
        const selectedOptions = [];
        
        // Only add individual selected caisses (not special options)
        field.options?.forEach(option => {
          if (!option.isSpecial && currentIds.includes(option.value)) {
            selectedOptions.push(option);
          }
        });
        
        const handleSpecialOptionClick = (specialValue, isSelected) => {
          // Mark that we're handling a special option to prevent onChange from interfering
          specialOptionHandledRef.current = true;
          
          if (specialValue === 'ALL_MOBILE') {
            const mobileIds = mobileCaisses.map(c => c.id);
            const physiqueIds = physiqueCaisses.map(c => c.id);
            
            if (isSelected) {
              // Deselect all mobile caisses, keep others
              const remainingIds = currentIds.filter(id => !mobileIds.includes(id));
              if (remainingIds.length === 0) {
                // Default to first physical caisse if nothing selected
                const firstPhysique = physiqueCaisses.length > 0 ? physiqueCaisses[0] : null;
                if (firstPhysique) {
                  setActualCaisseIds([firstPhysique.id]);
                  handleInputChange(field.name, [firstPhysique.id]);
                } else {
                  setActualCaisseIds([]);
                  handleInputChange(field.name, []);
                }
              } else {
                setActualCaisseIds(remainingIds);
                handleInputChange(field.name, remainingIds);
              }
            } else {
              // Select all mobile caisses, remove all physical
              const newIds = currentIds.filter(id => !physiqueIds.includes(id));
              mobileIds.forEach(id => {
                if (!newIds.includes(id)) {
                  newIds.push(id);
                }
              });
              setActualCaisseIds(newIds);
              handleInputChange(field.name, newIds);
            }
          } else if (specialValue === 'ALL_PHYSIQUE') {
            const mobileIds = mobileCaisses.map(c => c.id);
            const physiqueIds = physiqueCaisses.map(c => c.id);
            
            if (isSelected) {
              // Deselect all physical caisses, keep others
              const remainingIds = currentIds.filter(id => !physiqueIds.includes(id));
              if (remainingIds.length === 0) {
                // Default to first mobile caisse if nothing selected
                const firstMobile = mobileCaisses.length > 0 ? mobileCaisses[0] : null;
                if (firstMobile) {
                  setActualCaisseIds([firstMobile.id]);
                  handleInputChange(field.name, [firstMobile.id]);
                } else {
                  setActualCaisseIds([]);
                  handleInputChange(field.name, []);
                }
              } else {
                setActualCaisseIds(remainingIds);
                handleInputChange(field.name, remainingIds);
              }
            } else {
              // Select all physical caisses, remove all mobile
              const newIds = currentIds.filter(id => !mobileIds.includes(id));
              physiqueIds.forEach(id => {
                if (!newIds.includes(id)) {
                  newIds.push(id);
                }
              });
              setActualCaisseIds(newIds);
              handleInputChange(field.name, newIds);
            }
          }
          
          // Reset flag after onChange has had a chance to fire
          setTimeout(() => {
            specialOptionHandledRef.current = false;
          }, 200);
        };
        
        return (
          <div key={field.name} className="w-full">
            {showLabels && (
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#6B7280',
                  textTransform: 'none',
                  marginBottom: '8px',
                  letterSpacing: '0.5px'
                }}
              >
                {getFieldLabel(field.name)}
              </Typography>
            )}
            <Box ref={selectWrapperRef}>
              <Select
                isMulti
                options={field.options?.filter(opt => !opt.isSpecial).map((option) => ({
                  value: option.value,
                  label: option.label
                }))}
                value={selectedOptions.filter(opt => !opt.isSpecial).map(opt => ({ value: opt.value, label: opt.label }))}
                // Disable special options if they somehow get into options
                isOptionDisabled={(option) => option.value === 'ALL_MOBILE' || option.value === 'ALL_PHYSIQUE'}
                onChange={(selectedOptions) => {
                  if (isCaisseField) {
                    // If a special option was just handled, ignore this onChange
                    if (specialOptionHandledRef.current) {
                      return;
                    }
                    
                    const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    const mobileIds = mobileCaisses.map(c => c.id);
                    const physiqueIds = physiqueCaisses.map(c => c.id);
                    
                    // Filter out special option values - these are handled separately via onSpecialOptionClick
                    const regularSelectedIds = selectedValues.filter(
                      v => v !== 'ALL_MOBILE' && v !== 'ALL_PHYSIQUE'
                    );
                    
                    // Check if special options are in the selection
                    const hasAllMobile = selectedValues.includes('ALL_MOBILE');
                    const hasAllPhysique = selectedValues.includes('ALL_PHYSIQUE');
                    
                    // If special options are in the selection, handle them
                    if (hasAllMobile && hasAllPhysique) {
                      // Both special options selected - keep only the last one
                      const lastSpecialIndex = Math.max(
                        selectedValues.lastIndexOf('ALL_MOBILE'),
                        selectedValues.lastIndexOf('ALL_PHYSIQUE')
                      );
                      const lastSpecial = selectedValues[lastSpecialIndex];
                      
                      if (lastSpecial === 'ALL_MOBILE') {
                        // Keep only mobile caisses, remove physical
                        const newIds = [...regularSelectedIds.filter(id => !physiqueIds.includes(id)), ...mobileIds];
                        setActualCaisseIds(newIds);
                        handleInputChange(field.name, newIds);
                      } else {
                        // Keep only physical caisses, remove mobile
                        const newIds = [...regularSelectedIds.filter(id => !mobileIds.includes(id)), ...physiqueIds];
                        setActualCaisseIds(newIds);
                        handleInputChange(field.name, newIds);
                      }
                    } else if (hasAllMobile) {
                      // "Toutes les caisses mobiles" selected - select all mobile caisses
                      const newIds = [...regularSelectedIds.filter(id => !mobileIds.includes(id) && !physiqueIds.includes(id)), ...mobileIds];
                      setActualCaisseIds(newIds);
                      handleInputChange(field.name, newIds);
                    } else if (hasAllPhysique) {
                      // "Toutes les caisses physiques" selected - select all physical caisses
                      const newIds = [...regularSelectedIds.filter(id => !mobileIds.includes(id) && !physiqueIds.includes(id)), ...physiqueIds];
                      setActualCaisseIds(newIds);
                      handleInputChange(field.name, newIds);
                    } else {
                      // No special options - user is selecting/deselecting individual items
                      setActualCaisseIds(regularSelectedIds);
                      handleInputChange(field.name, regularSelectedIds);
                    }
                  } else {
                    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    handleInputChange(field.name, values);
                  }
                }}
                placeholder={field.placeholder}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                styles={{
                  ...getCustomSelectStyles(field.name, true),
                }}
                filterOption={createFilter({
                  ignoreCase: true,
                  trim: true,
                  matchFrom: 'any',
                })}
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => {
                  setMenuIsOpen(true);
                  lastActionRef.current = 'menu-open';
                }}
                onMenuClose={() => {
                  setMenuIsOpen(false);
                  lastActionRef.current = 'menu-close';
                  if (typeof window !== 'undefined') {
                    window.__multiselectScrollPosition = null;
                  }
                }}
                onInputChange={(inputValue, { action }) => {
                  lastActionRef.current = action;
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                selectProps={{
                  mobileCaisses: isCaisseField ? mobileCaisses : undefined,
                  physiqueCaisses: isCaisseField ? physiqueCaisses : undefined,
                  actualIds: isCaisseField ? actualCaisseIds : undefined,
                  onSpecialOptionClick: isCaisseField ? handleSpecialOptionClick : undefined,
                  menuIsOpen: menuIsOpen
                }}
                components={{
                  MenuList: isCaisseField ? (props) => (
                    <MultiselectMenuList 
                      {...props}
                      mobileCaisses={mobileCaisses}
                      physiqueCaisses={physiqueCaisses}
                      actualIds={actualCaisseIds}
                      onSpecialOptionClick={handleSpecialOptionClick}
                      menuIsOpen={menuIsOpen}
                    />
                  ) : undefined,
                  Option: (props) => <MultiselectOption {...props} />,
                  MultiValue: () => null,
                  ValueContainer: (props) => (
                    <MultiselectValueContainer 
                      {...props} 
                      getValue={() => isCaisseField ? actualCaisseIds : (formData[field.name] || [])}
                      countColor="#000000"
                    />
                  ),
                  ClearIndicator: () => null,
                  DropdownIndicator: (props) => (
                    <CustomDropdownIndicator 
                      {...props} 
                      hasValue={multiselectHasValue}
                      onClear={() => {
                        if (isCaisseField) {
                          setActualCaisseIds([]);
                        }
                        handleInputChange(field.name, []);
                      }}
                      isMulti={true}
                    />
                  ),
                }}
              />
            </Box>
            {renderErrorDisplay(field.name)}
          </div>
        );
      }

      case 'hidden':
        return null; // Don't render hidden fields

      default:
        return (
          <div key={field.name} className="w-full">
            {showLabels && (
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#6B7280',
                  textTransform: 'none',
                  marginBottom: '8px',
                  letterSpacing: '0.5px'
                }}
              >
                {getFieldLabel(field.name)}
              </Typography>
            )}
            <TextField
              {...commonProps}
              placeholder={field.placeholder}
              autoComplete={field.name === 'username' ? 'new-username' : field.name === 'password' ? 'new-password' : 'off'}
              inputProps={{
                autoComplete: field.name === 'username' ? 'new-username' : field.name === 'password' ? 'new-password' : 'off',
                'data-form-type': 'other',
                ...field.inputProps
              }}
              sx={{
                ...commonProps.sx,
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  backgroundColor: '#ffffff',
                  border: hasError ? '2px solid #EF456E' : (hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3'),
                  borderRadius: '8px',
                  '&:hover': {
                    border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
                  },
                  '&.Mui-focused': {
                    border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '0 !important',
                  '&::placeholder': {
                    color: '#A3A3A3',
                    opacity: 1,
                  },
                },
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
            />
            {renderErrorDisplay(field.name)}
          </div>
        );
    }
  };

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Check if all required fields are filled
      const allFieldsFilled = resolvedFields.every(field => {
        if (!field.required) return true;
        
        const value = formData[field.name];
        if (field.type === 'select' || field.type === 'multiselect') {
          return value !== null && value !== undefined && value !== '' && 
                 !(Array.isArray(value) && value.length === 0);
        }
        return value && value.toString().trim() !== '';
      });
      
      if (allFieldsFilled) {
        e.preventDefault();
        handleSubmit();
      }
      // If not all fields filled, let Enter work normally (dropdowns, etc.)
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onKeyDown={handleDialogKeyDown}
      maxWidth="md"
      sx={{
        zIndex: 1600, // Above DetailsPanel (1500) so edit modal from detail view stays on top
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '20vh', // Position around 1/3 from top
        }
      }}
      slotProps={{
        root: {
          sx: { zIndex: 1600 }
        }
      }}
      PaperProps={{
        sx: {
          background: '#F9FAFC',
          boxShadow: '0px 20px 80px #00000011',
          borderRadius: '12px',
          opacity: 1,
          width: resolvedFields.length <= 1 ? '600px' : resolvedFields.length >= 6 ? '1000px' : '800px', // Bigger width for category modal
          maxWidth: 'none',
          maxHeight: 'none',
          margin: 0, // Remove default margin
        }
      }}
    >
      <DialogContent sx={{ p: 0, height: '100%', position: 'relative' }}>

        {/* Header with Title and Toggle */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3, 
          pt: 3, 
          px: 5 
        }}>
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '24px',
              fontWeight: 600,
              color: '#36495C',
              textTransform: 'none',
              lineHeight: 1, // Ensure consistent line height
              mr: 4, // Add margin-right for spacing
            }}
          >
            {title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {toggleConfig && (
              <Toggle
                checked={formData[toggleConfig.field]}
                onChange={() => setFormData(prev => ({ ...prev, [toggleConfig.field]: !prev[toggleConfig.field] }))}
                label={formData[toggleConfig.field] ? toggleConfig.activeLabel || 'Activé' : toggleConfig.inactiveLabel || 'Désactivé'}
              />
            )}
            <CloseIcon
              onClick={handleClose}
              sx={{
                cursor: 'pointer',
                color: '#36495C',
                fontSize: '24px',
                lineHeight: 1, // Ensure consistent line height
              }}
            />
          </Box>
        </Box>

        {/* Form Fields */}
        <Box component="form" autoComplete="off" sx={{ px: 5, pb: 2 }}>
          {/* First Row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {resolvedFields.slice(0, fieldsPerRow).map((field, index) => (
              <Box key={field.name} sx={{ flex: '1 1 0', minWidth: 0 }}>
                {renderField(field, index)}
              </Box>
            ))}
          </Box>

          {/* Second Row */}
          {resolvedFields.length > fieldsPerRow && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {resolvedFields.slice(fieldsPerRow, fieldsPerRow * 2).map((field, index) => (
                <Box key={field.name} sx={{ flex: '1 1 0', minWidth: 0 }}>
                  {renderField(field, index + fieldsPerRow)}
                </Box>
              ))}
            </Box>
          )}

          {/* Third Row (for any remaining fields) */}
          {resolvedFields.length > fieldsPerRow * 2 && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {resolvedFields.slice(fieldsPerRow * 2).map((field, index) => (
                <Box key={field.name} sx={{ flex: '1 1 0', minWidth: 0 }}>
                  {renderField(field, index + fieldsPerRow * 2)}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            alignItems: 'center',
            px: 5,
            pb: 3
          }}
        >
          <ActionButton
            variant="text"
            onClick={handleClose}
            sx={{
              color: '#36495C',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 'auto',
              px: 2
            }}
          >
            Annuler
          </ActionButton>

          <ActionButton
            variant="contained"
            onClick={handleSubmit}
            loading={loading}
            sx={{
              width: '318px',
              height: '48px',
              backgroundColor: '#027BC6',
              boxShadow: '0px 4px 10px #BEBEBE50',
              borderRadius: '12px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#025a9c'
              }
            }}
          >
            {submitButtonText}
          </ActionButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreationModal;